import { RootState } from "@/redux/store";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { QueryAnswerType } from "../FpAppV4/utils/fpAppV4Interface";
import ChartViewer, { processChartData } from "../FpAppV4/ChartViewer";
import { ErrorBoundary } from "react-error-boundary";

const HANDLE_SIZE = 10;

type HandlePosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight";

const handlePositions: {
  pos: HandlePosition;
  style: React.CSSProperties;
  cursor: string;
}[] = [
  {
    pos: "top",
    style: {
      top: -HANDLE_SIZE / 2,
      left: "50%",
      transform: "translateX(-50%)",
    },
    cursor: "ns-resize",
  },
  {
    pos: "bottom",
    style: {
      bottom: -HANDLE_SIZE / 2,
      left: "50%",
      transform: "translateX(-50%)",
    },
    cursor: "ns-resize",
  },
  {
    pos: "left",
    style: {
      left: -HANDLE_SIZE / 2,
      top: "50%",
      transform: "translateY(-50%)",
    },
    cursor: "ew-resize",
  },
  {
    pos: "right",
    style: {
      right: -HANDLE_SIZE / 2,
      top: "50%",
      transform: "translateY(-50%)",
    },
    cursor: "ew-resize",
  },
  {
    pos: "topLeft",
    style: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 },
    cursor: "nwse-resize",
  },
  {
    pos: "topRight",
    style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 },
    cursor: "nesw-resize",
  },
  {
    pos: "bottomLeft",
    style: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 },
    cursor: "nesw-resize",
  },
  {
    pos: "bottomRight",
    style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 },
    cursor: "nwse-resize",
  },
];

// Fullscreen Modal Component
const ChartModal = ({
  isOpen,
  onClose,
  llmData,
}: {
  isOpen: boolean;
  onClose: () => void;
  llmData: any;
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl relative"
        style={{ width: "85vw", height: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors duration-200"
        >
          <svg
            className="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="p-6 h-full">
          <ChartViewer llmData={llmData} />
        </div>
      </div>
    </div>
  );
};

/** Find the outermost Tiptap-generated wrapper (div.react-renderer). */
function getOuterDiv(el: HTMLElement | null): HTMLElement | null {
  return el?.closest(".react-renderer") as HTMLElement | null;
}

const ChartInEditor = ({
  node,
  updateAttributes,
}: {
  node: any;
  updateAttributes: (attrs: Record<string, any>) => void;
}) => {
  const {
    qid,
    id: chartId,
    width: attrWidth,
    height: attrHeight,
    align: attrAlign,
  } = node.attrs;
  const queryAnswers: QueryAnswerType[] = useSelector(
    (state: RootState) => state.fpAppV4Data.queryAnswers
  );
  const queryAnswer = queryAnswers.find((qa: any) => qa.qid === qid);
  const chartData = queryAnswer?.toolResponse?.answer?.chartData?.[chartId];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const llmData = chartData;

  const processedResult = processChartData(llmData);
  const isValidChartData = processedResult !== null;

  // Keep the outer react-renderer div sized and aligned to match node attrs.
  // Runs on every render so it self-heals if Tiptap/ProseMirror resets styles.
  useEffect(() => {
    const outer = getOuterDiv(containerRef.current);
    if (!outer) return;
    outer.style.width = attrWidth ? `${attrWidth}%` : "100%";
    // When chart has LLM-set aspect ratio, let the browser compute height via
    // CSS aspect-ratio (auto-resizes on container width changes too)
    const aspectRatio = processedResult?.chartData?.options?.aspectRatio;
    if (aspectRatio != null) {
      outer.style.aspectRatio = String(aspectRatio);
      outer.style.height = "auto";
    } else {
      outer.style.aspectRatio = "";
      outer.style.height = `${attrHeight ?? 450}px`;
    }

    const align = attrAlign ?? "center";

    outer.style.float = "none";
    outer.style.clear = "both";
    if (align === "left") {
      outer.style.marginLeft = "0";
      outer.style.marginRight = "auto";
    } else if (align === "right") {
      outer.style.marginLeft = "auto";
      outer.style.marginRight = "0";
    } else {
      outer.style.marginLeft = "auto";
      outer.style.marginRight = "auto";
    }
  });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, position: HandlePosition) => {
      e.preventDefault();
      e.stopPropagation();

      const outer = getOuterDiv(containerRef.current);
      if (!outer) return;

      const editorContainer = outer.closest(
        ".ProseMirror"
      ) as HTMLElement | null;
      const editorWidth =
        editorContainer?.clientWidth ?? outer.parentElement?.clientWidth ?? 800;

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = outer.offsetWidth;
      const startHeight = outer.offsetHeight;

      // Which handles affect width / height
      const affectsWidth = [
        "left",
        "right",
        "topLeft",
        "topRight",
        "bottomLeft",
        "bottomRight",
      ].includes(position);
      const affectsHeight = [
        "top",
        "bottom",
        "topLeft",
        "topRight",
        "bottomLeft",
        "bottomRight",
      ].includes(position);

      // For left-side handles, dragging left (negative dx) = wider
      const widthSign = ["left", "topLeft", "bottomLeft"].includes(position)
        ? -1
        : 1;
      // For top-side handles, dragging up (negative dy) = taller
      const heightSign = ["top", "topLeft", "topRight"].includes(position)
        ? -1
        : 1;

      // Whether this handle touches the top/left edge — needs visual offset
      // so the opposite edge appears anchored during drag
      const isTopEdge = ["top", "topLeft", "topRight"].includes(position);
      const isLeftEdge = ["left", "topLeft", "bottomLeft"].includes(position);

      const onMouseMove = (moveEvent: MouseEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;

        if (affectsWidth) {
          newWidth = Math.max(
            200,
            Math.min(startWidth + dx * widthSign, editorWidth)
          );
        }

        if (affectsHeight) {
          newHeight = Math.max(150, startHeight + dy * heightSign);
        }

        // Direct DOM update for smooth dragging
        outer.style.width = `${newWidth}px`;
        outer.style.height = `${newHeight}px`;

        // For top/left-edge handles, use transform to visually anchor the
        // opposite edge. Without this, the element's top-left corner stays
        // fixed in the document flow and the bottom/right edge moves instead.
        const offsetX = isLeftEdge ? startWidth - newWidth : 0;
        const offsetY = isTopEdge ? startHeight - newHeight : 0;
        if (offsetX || offsetY) {
          outer.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        }
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        // Clear the visual offset — useEffect re-applies correct styles
        outer.style.transform = "";

        const finalWidth = outer.offsetWidth;
        const finalHeight = outer.offsetHeight;

        // Convert to percentage for persistence
        let widthPct = Math.round((finalWidth / editorWidth) * 100);
        widthPct = Math.max(20, Math.min(100, widthPct));

        // Commit to Tiptap — the useEffect will then re-apply on next render
        updateAttributes({ width: widthPct, height: finalHeight });
      };

      document.body.style.cursor =
        handlePositions.find((h) => h.pos === position)?.cursor ?? "default";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [updateAttributes]
  );

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  if (!isValidChartData) {
    return null;
  }

  return (
    <>
      <div
        ref={containerRef}
        className="relative w-full h-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          border: isHovered ? "1.5px solid #4285f4" : "1px solid transparent",
          borderRadius: 4,
          transition: "border-color 0.15s",
        }}
      >
        {/* Resize handles */}
        {isHovered &&
          handlePositions.map(({ pos, style, cursor }) => (
            <div
              key={pos}
              onMouseDown={(e) => handleMouseDown(e, pos)}
              style={{
                position: "absolute",
                zIndex: 20,
                width: HANDLE_SIZE,
                height: HANDLE_SIZE,
                cursor,
                ...style,
              }}
            >
              <div
                style={{
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  borderRadius: "50%",
                  background: "#4285f4",
                  border: "1.5px solid white",
                  boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                }}
              />
            </div>
          ))}

        {/* Alignment toolbar */}
        {isHovered && (
          <div
            className="absolute -bottom-10 left-1/2 flex items-center gap-1 bg-white rounded-lg shadow-md border border-gray-200 px-1 py-1 z-20"
            style={{ transform: "translateX(-50%)" }}
          >
            {(["left", "center", "right"] as const).map((a) => (
              <button
                key={a}
                onClick={() => updateAttributes({ align: a })}
                className={`p-1.5 rounded ${
                  (attrAlign ?? "center") === a
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                title={`Align ${a}`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  {a === "left" && (
                    <>
                      <path d="M3 6h18M3 12h12M3 18h18" />
                    </>
                  )}
                  {a === "center" && (
                    <>
                      <path d="M3 6h18M6 12h12M3 18h18" />
                    </>
                  )}
                  {a === "right" && (
                    <>
                      <path d="M3 6h18M9 12h12M3 18h18" />
                    </>
                  )}
                </svg>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={() => setIsModalOpen(true)}
          className="absolute top-2 right-2 z-10 bg-white hover:bg-gray-50 rounded-lg px-3 py-2 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg flex items-center gap-2"
          title="Expand chart to fullscreen"
        >
          <svg
            className="w-4 h-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
            />
          </svg>
          <span className="text-sm text-gray-600 font-medium">
            Fullscreen Chart
          </span>
        </button>

        <ErrorBoundary
          fallbackRender={({ error }) => <div>{error.message}</div>}
        >
          <div className="w-full h-full">
            <ChartViewer llmData={llmData} />
          </div>
        </ErrorBoundary>
      </div>

      <ChartModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        llmData={llmData}
      />
    </>
  );
};

export default React.memo(ChartInEditor);
